<template>
  <ul>
    <li
      v-for="(navGroupItem, index) in firstLevelMenu.items"
      :key="`${navGroupItem.key}__${index}`"
    >
      <navigation-sidebar-link-group
        :nav-group-item="navGroupItem"
        :hide-group-label="firstLevelMenu.items.length === 1"
      />
    </li>
  </ul>
</template>

<script lang="ts" setup>
import useNavigationDataAnonymous from '#variant/components/navigation/sidebar/features/useNavigationDataAnonymous';
import useNavigationDataAuthenticated from '#variant/components/navigation/sidebar/features/useNavigationDataAuthenticated';

const authStore = useAuthStore();

// The auth state the *server* rendered the sidebar with. Captured via useState
// so it is serialised into the Nuxt payload and is hydration-stable: reading
// authStore.isAuthenticated directly would be racy, because a client-side
// session recovery can flip it before this component's setup runs.
const ssrIsAuthenticated = useState(
  'sidebar:first-level-ssr-auth',
  () => authStore.isAuthenticated,
);

// Register the lifecycle hook BEFORE the awaits below — in an async-setup
// component a hook added after an await is not bound to the instance and never
// fires. isHydrated stays false through the initial (hydration) render.
const isHydrated = ref(false);
onMounted(() => {
  isHydrated.value = true;
});

// Resolve both menus and switch between them reactively. The session can be
// recovered client-side after the first (anonymous) SSR render — e.g. when the
// browser is reopened: SSR has no gp_at cookie, so it renders anonymous, then
// the keycloak-auth library refreshes the token and flips isAuthenticated to
// true. A non-reactive `isAuthenticated ? ... : ...` branch here would keep the
// anonymous menu (no account section) until a full reload (F5).
const [
  { firstLevelMenu: authenticatedMenu },
  { firstLevelMenu: anonymousMenu },
] = await Promise.all([
  useNavigationDataAuthenticated(),
  useNavigationDataAnonymous(),
]);

// Render the menu the server rendered until hydration is finished, then follow
// the live auth state. Recovery can flip authStore.isAuthenticated *during*
// hydration; switching the menu mid-hydration makes Vue reconcile authenticated
// content against the anonymous SSR DOM, which updates the visible text but
// leaves the per-item data-testid attributes frozen at their SSR (anonymous)
// values. Deferring the swap to a post-mount patch keeps text and attributes
// in sync.
const firstLevelMenu = computed(() =>
  (isHydrated.value ? authStore.isAuthenticated : ssrIsAuthenticated.value)
    ? authenticatedMenu.value
    : anonymousMenu.value,
);
</script>
